import { useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';

import useUserAuth from '@webclient/components/user-authentication/hooks/useUserAuth';
import useValidateResetToken from '@webclient/components/user-authentication/hooks/useValidateResetToken';
import BaseStep from '@webclient/components/user-authentication/sections/steps/components/base-step';
import RHFPinCode, {
  defaultValues,
  ValidateCodeFormType,
} from '@webclient/components/user-authentication/sections/steps/components/pin-code';
import ResetPassword from '@webclient/components/user-authentication/sections/steps/reset-password';

import { Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import { useForm } from 'react-hook-form';

export default function ValidateCode() {
  const { t } = useTranslate();
  const { email, handleSetResetToken } = useUserAuth();
  const { mutateAsync: validateResetToken, isPending: isValidatePending } =
    useValidateResetToken();
  const [isCodeValid, setIsCodeValid] = useState(false);

  const methods = useForm<ValidateCodeFormType>({
    defaultValues,
    mode: 'onChange',
  });

  const { handleSubmit, watch } = methods;

  const allFieldsFilled = watch('code').every((value) => value !== '');

  async function onSubmit(data: ValidateCodeFormType) {
    const token = data.code.join('');
    handleSetResetToken(token);
    try {
      await validateResetToken({ email, token });
      setIsCodeValid(true);
    } catch (error) {
      setIsCodeValid(false);
      console.error('Error validating code', error);
    }
  }

  if (isCodeValid) {
    return <ResetPassword />;
  }

  return (
    <BaseStep
      slots={{
        header: {
          withBackButton: true,
          heading: t('layout.auth.steps.validateCode.form.label', 'Enter code'),
        },

        form: {
          methods: methods,
          onSubmit: handleSubmit(onSubmit),
        },
      }}
    >
      <Text variant="textSm">
        {t(
          'layout.auth.steps.validateCode.form.description',
          'We sent a code to your email {email} Please enter your 6-digit code to verify your identity. Your code will expire in 10 minutes.',
          {
            email,
          },
        )}
      </Text>

      <RHFPinCode {...methods} />

      <LoadingButton
        sx={{ marginBlockStart: 1 }}
        size="xlarge"
        onClick={handleSubmit(onSubmit)}
        variant="contained"
        fullWidth
        disabled={!allFieldsFilled}
        loading={isValidatePending}
      >
        {t('layout.auth.steps.validateCode.form.button', 'Verify')}
      </LoadingButton>
    </BaseStep>
  );
}
